// visitenkarteFirmensuche.ts — gibt es die Firma der Karte schon im Bestand?
//
// P3 (Plan 2026-09-30-ocr-scan-kontaktanlage): Die Firma wird über den Namen
// gesucht — auch unscharf, weil die Karte „WIN.DN GmbH" druckt, der Bestand
// aber „WINDN GmbH" oder „Win.DN" führt — und über die Domain von Website bzw.
// E-Mail. Bis 09/2026 zählte nur ein exakt gleicher Name; eine leicht anders
// geschriebene Firma wurde deshalb ein zweites Mal angelegt.
//
// Ein unscharfer Treffer wird nie still verknüpft: Der Dialog zeigt die Treffer
// zur Auswahl (samt „als neue Firma anlegen"). Die Person an die falsche Firma
// zu hängen, fällt später schwerer auf als eine doppelt angelegte Firma.

/** Rechtsform-Zusätze und Füllwörter, die beim Namensvergleich nicht zählen. */
const ZUSAETZE = new Set([
  "gmbh",
  "mbh",
  "ag",
  "kg",
  "kgaa",
  "ohg",
  "gbr",
  "ug",
  "haftungsbeschraenkt",
  "ek",
  "ev",
  "eg",
  "se",
  "co",
  "und",
  "partg",
  "partgmbb",
  "ltd",
  "inc",
  "llc",
  "sarl",
  "bv",
  "nv",
]);

/** Freemail-Domains verraten nichts über die Firma. */
const FREEMAIL = new Set([
  "gmail.com",
  "googlemail.com",
  "web.de",
  "gmx.de",
  "gmx.net",
  "gmx.at",
  "t-online.de",
  "outlook.com",
  "outlook.de",
  "hotmail.com",
  "hotmail.de",
  "live.de",
  "live.com",
  "yahoo.com",
  "yahoo.de",
  "icloud.com",
  "me.com",
  "aol.com",
  "freenet.de",
  "posteo.de",
  "mail.de",
  "online.de",
  "arcor.de",
]);

const falten = (s: string): string =>
  s
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

/**
 * Der Kern eines Firmennamens: ohne Rechtsform, Satzzeichen und Groß-/
 * Kleinschreibung. „WIN.DN GmbH" und „Windn GmbH" haben denselben Kern.
 */
export function firmenKern(name: string): string {
  return falten(name)
    .replace(/&/g, " ")
    .split(/[\s,;/()+-]+/)
    .map((w) => w.replace(/[^a-z0-9]/g, ""))
    .filter((w) => w && !ZUSAETZE.has(w))
    .join("");
}

/** Wörter, unter denen im Bestand gesucht wird (der Name ohne Rechtsform). */
export function firmenSuchbegriff(name: string): string {
  return name
    .split(/\s+/)
    .filter((w) => {
      const kern = falten(w).replace(/[^a-z0-9]/g, "");
      // Reine Zeichen („&", „-") und Rechtsformen engen die Suche nur ein.
      return kern !== "" && !ZUSAETZE.has(kern);
    })
    .join(" ")
    .trim();
}

const abstand = (a: string, b: string): number => {
  if (a === b) return 0;
  let vorige = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 0; i < a.length; i++) {
    const aktuelle = [i + 1];
    for (let j = 0; j < b.length; j++) {
      aktuelle.push(Math.min(aktuelle[j] + 1, vorige[j + 1] + 1, vorige[j] + (a[i] === b[j] ? 0 : 1)));
    }
    vorige = aktuelle;
  }
  return vorige[b.length];
};

export type NamensNaehe = "genau" | "aehnlich" | null;

/** Wie nah sind sich zwei Firmennamen? */
export function namensNaehe(karte: string, bestand: string): NamensNaehe {
  const a = firmenKern(karte);
  const b = firmenKern(bestand);
  if (!a || !b) return null;
  if (a === b) return "genau";
  const kurz = Math.min(a.length, b.length);
  // „Müller & Schäfer" ↔ „Müller & Schäfer Bauunternehmung": der eine Kern
  // steckt im anderen. Erst ab fünf Zeichen — „AB" steckt in allem.
  if (kurz >= 5 && (a.includes(b) || b.includes(a))) return "aehnlich";
  // Tippfehler: bis zu 1 Abweichung je 6 Zeichen.
  if (kurz >= 5 && abstand(a, b) <= Math.floor(Math.max(a.length, b.length) / 6)) return "aehnlich";
  return null;
}

/** Domain aus Website oder E-Mail — ohne Freemail-Anbieter. */
export function firmenDomain(befund: {
  website?: string;
  emailZentrale?: string;
  email?: string;
}): string | undefined {
  const aus = (wert?: string): string | undefined => {
    const d = String(wert ?? "")
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .split("@")
      .pop()!
      .split(/[/?#]/)[0];
    return d && d.includes(".") && !FREEMAIL.has(d) ? d : undefined;
  };
  return aus(befund.website) ?? aus(befund.emailZentrale) ?? aus(befund.email);
}

/** Ein Firmenkontakt aus der Bestandssuche. */
export type BestandsFirma = {
  id: number;
  name: string;
  /** Kommunikationswerte, falls die Suche sie mitliefert (für den Domainabgleich). */
  kanaele?: string[];
};

export type FirmenTreffer = BestandsFirma & {
  /** Warum die Firma als Treffer gilt — steht im Dialog dabei. */
  grund: "name" | "name-aehnlich" | "domain";
};

/**
 * Bewertet die Suchergebnisse: Namenstreffer (genau, dann ähnlich), dann
 * Domain-Treffer. Doppelte Firmen erscheinen einmal, mit dem stärksten Grund.
 *
 * @param domainErgebnisse Ergebnisse der Suche nach der Domain. Liefert die
 *   Suche die Kanäle mit, muss die Domain darin vorkommen; sonst gilt der
 *   Suchtreffer selbst als Beleg.
 */
export function firmenTrefferBewerten(args: {
  firmenname: string;
  domain?: string;
  namensErgebnisse: BestandsFirma[];
  domainErgebnisse?: BestandsFirma[];
}): FirmenTreffer[] {
  const { firmenname, domain, namensErgebnisse, domainErgebnisse = [] } = args;
  const nachId = new Map<number, FirmenTreffer>();
  const rang = { name: 0, "name-aehnlich": 1, domain: 2 } as const;
  const merken = (f: BestandsFirma, grund: FirmenTreffer["grund"]) => {
    const vorher = nachId.get(f.id);
    if (!vorher || rang[grund] < rang[vorher.grund]) nachId.set(f.id, { ...f, grund });
  };

  for (const f of namensErgebnisse) {
    const naehe = firmenname ? namensNaehe(firmenname, f.name) : null;
    if (naehe === "genau") merken(f, "name");
    else if (naehe === "aehnlich") merken(f, "name-aehnlich");
  }
  if (domain) {
    for (const f of domainErgebnisse) {
      const belegt = !f.kanaele || f.kanaele.some((k) => k.toLowerCase().includes(domain));
      if (belegt) merken(f, "domain");
    }
  }
  return [...nachId.values()].sort((a, b) => rang[a.grund] - rang[b.grund] || a.name.localeCompare(b.name));
}

/** Suchergebnis der Kontaktliste → Firmen (nur Typ „Firma"). */
export function alsBestandsFirmen(liste: unknown): BestandsFirma[] {
  if (!Array.isArray(liste)) return [];
  return liste
    .filter((c: any) => c?.type?.type === "Firma" && Number(c?.id) > 0)
    .map((c: any) => ({
      id: Number(c.id),
      name: String(c.lastname ?? c.displayName ?? "").trim(),
      ...(Array.isArray(c.communications)
        ? { kanaele: c.communications.map((k: any) => String(k?.value ?? "")).filter(Boolean) }
        : {}),
    }))
    .filter((f) => f.name);
}
